import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { InfinitySpin } from 'react-loader-spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faEnvelope, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

function Contact() {
  const [submitted, setSubmitted] = useState(false);

  return (
    <section id="contact" className="py-8" style={{ backgroundColor: 'var(--bg-surface)' }}>
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="title-box text-center section-header-reveal mb-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight" style={{ color: 'var(--text-primary)' }}>
            Get In Touch
          </h2>
          <div className="w-16 h-1 mx-auto mb-6 rounded-full" style={{ backgroundColor: 'var(--primary)' }}></div>
          <p className="text-lg leading-relaxed max-w-2xl mx-auto text-reveal reveal-delay-1" style={{ color: 'var(--text-secondary)' }}>
            Whether you have a question, want to collaborate, or just want to connect — I&apos;d love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Information */}
          <div className="space-y-8 scroll-reveal-left reveal-delay-2">
            <div className="rounded-lg p-8" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                Contact Information
              </h3>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--bg-surface-soft)', border: '1px solid var(--border)' }}>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-lg" style={{ color: 'var(--primary)' }} />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Location</h4>
                    <p style={{ color: 'var(--text-secondary)' }}>Chattogram, Bangladesh</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--bg-surface-soft)', border: '1px solid var(--border)' }}>
                    <FontAwesomeIcon icon={faEnvelope} className="text-lg" style={{ color: 'var(--primary)' }} />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Email</h4>
                    <a
                      href="mailto:m.sayem.c@gmail.com"
                      className="font-semibold transition-colors hover:opacity-80"
                      style={{ color: 'var(--primary)' }}
                    >
                      m.sayem.c@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action Card */}
            <div className="cta-card rounded-lg p-6 scroll-reveal-scale reveal-delay-3" style={{ backgroundColor: 'var(--primary)', border: '1px solid transparent' }}>
              <h3 className="text-lg font-semibold mb-2 btn-text" style={{ color: '#ffffff' }}>Let&apos;s work together</h3>
              <p className="text-sm leading-relaxed btn-text" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                I&apos;m open to new opportunities and collaborations. Feel free to reach out.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="rounded-lg p-8 scroll-reveal-right reveal-delay-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'var(--primary)' }}>
                  <FontAwesomeIcon icon={faPaperPlane} className="text-2xl text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Message Sent!</h3>
                <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Thank you for reaching out. I&apos;ll get back to you as soon as possible.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-2 font-semibold rounded-lg transition-colors text-white hover:opacity-80"
                  style={{ backgroundColor: 'var(--primary)' }}
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <div>
                <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Send me a message</h3>
                <Formik
                  initialValues={{
                    name: '',
                    message: '',
                  }}
                  validationSchema={Yup.object({
                    name: Yup.string()
                      .min(4, 'Must be at least 4 characters')
                      .required('Required'),
                    message: Yup.string()
                      .required('Required'),
                  })}
                  onSubmit={(values, formik) => {
                    formik.setSubmitting(true);
                    fetch('https://formspree.io/xqkygvjg', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify(values),
                    })
                      .then(res => {
                        if (res.ok) {
                          setSubmitted(true);
                          formik.resetForm();
                        } else {
                          // show error message
                        }
                        formik.setSubmitting(false);
                      })
                      .catch(error => {
                        formik.setSubmitting(false);
                      });
                  }}
                >
                  {formik => (
                    <Form className="space-y-6">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-semibold mb-2"
                          style={{ color: 'var(--text-muted)' }}
                        >
                          Your Name *
                        </label>
                        <Field
                          name="name"
                          type="text"
                          className="w-full px-4 py-3 rounded-lg focus:ring-2 transition-colors"
                          style={{
                            border: `1px solid var(--border)`,
                            backgroundColor: 'var(--bg-main)',
                            color: 'var(--text-primary)',
                            focusRing: 'var(--focus)'
                          }}
                          id="name"
                          placeholder="Enter your full name"
                        />
                        {formik.touched.name && formik.errors.name ? (
                          <div className="text-red-500 text-sm mt-1">{formik.errors.name}</div>
                        ) : null}
                      </div>

                      <div>
                        <label
                          htmlFor="message"
                          className="block text-sm font-semibold mb-2"
                          style={{ color: 'var(--text-muted)' }}
                        >
                          Message *
                        </label>
                        <Field
                          component="textarea"
                          className="w-full px-4 py-3 rounded-lg focus:ring-2 transition-colors resize-vertical"
                          style={{
                            border: `1px solid var(--border)`,
                            backgroundColor: 'var(--bg-main)',
                            color: 'var(--text-primary)',
                            focusRing: 'var(--focus)'
                          }}
                          name="message"
                          id="message"
                          rows="5"
                          placeholder="Tell me about your project, idea, or just say hello..."
                        />
                        {formik.touched.message && formik.errors.message ? (
                          <div className="text-red-500 text-sm mt-1">{formik.errors.message}</div>
                        ) : null}
                      </div>

                      <button
                        type="submit"
                        disabled={formik.isSubmitting}
                        className="w-full font-semibold py-3 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        style={{
                          backgroundColor: '#2563eb',
                          color: '#ffffff',
                          border: 'none'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#2563eb'}
                      >
                        {formik.isSubmitting ? (
                          <>
                            <InfinitySpin width="20" color="#ffffff" />
                            <span className="ml-2 btn-text" style={{ color: '#ffffff' }}>Sending...</span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faPaperPlane} className="mr-2 btn-text" style={{ color: '#ffffff' }} />
                            <span className="btn-text" style={{ color: '#ffffff' }}>Send Message</span>
                          </>
                        )}
                      </button>
                    </Form>
                  )}
                </Formik>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
